import React from 'react';
import classNames from 'classnames';
import SvgChevron from '@app/shared/icons/Chevron';
import styles from '../../opm-summary-waterfall-erf.module.scss';
import {
  FirstLastInstrument,
  OpmFirstLastWaterfallDictionaryDto,
} from '@app/shared/models/contracts/opm-calculation-results-dto';
import { useLocale } from '@app/core/hooks/useLocale';
import TrancheCalculationsErf from '../../first-last-5-erf/tranche-calculations-erf/TrancheCalculationsErf';
import InstrumentExitDistributionsTotals from './InstrumentExitDistributionsTotals';
import HorizontalTableDivider from '@app/shared/components/table/HorizontalTableDivider';
import { TrancheLevelRow } from './row-components/TrancheLevelRow';
import InstrumentLevelRow from './row-components/InstrumentLevelRow';

interface InstrumentExitDistributionsProps {
  expanded: { [key: string]: boolean };
  handleToggle: (key: string) => void;
  titleColSpan: number;
  opmWaterfall: OpmFirstLastWaterfallDictionaryDto;
  selectedYear: string;
  waterfallIterations: number;
  instrumentHasTranches: boolean;
  instrumentHasDloc: boolean;
  instrumentKey: string;
  instrument: FirstLastInstrument;
  interpolationOption: { escapeValue: boolean };
}

const InstrumentExitDistributions: React.FC<InstrumentExitDistributionsProps> = ({
  expanded,
  handleToggle,
  titleColSpan,
  opmWaterfall,
  selectedYear,
  waterfallIterations,
  instrumentHasTranches,
  instrumentHasDloc,
  instrumentKey,
  instrument,
  interpolationOption,
}) => {
  const { l } = useLocale();
  const firstSimulation = Object.keys(opmWaterfall[selectedYear].first)[0];
  const instrumentName = instrument.instrumentName;
  const [currentTrancheIndex, setCurrentTrancheIndex] = React.useState(0);
  const isOrdinaryEquity = instrument.instrumentType === 'OrdinaryEquity';
  const firstSection = opmWaterfall[selectedYear]?.first;
  const firstSectionFirstItem = firstSection[Object.keys(firstSection)[0]];
  const instrumentData = firstSectionFirstItem
    ? firstSectionFirstItem.instruments[instrumentKey]
    : undefined;
  const hasInfusions = (instrumentData?.infusionTransactions?.length ?? 0) > 0;
  const hasRedemptions = (instrumentData?.redemptionTransactions?.length ?? 0) > 0;
  const totalCashPay = instrumentData?.transactionSummary?.totalCashPay ?? 0;
  const totalDividends = instrumentData?.transactionSummary?.totalDividends ?? 0;
  const hasCashPay = Boolean(totalCashPay);
  const hasDividends = Boolean(totalDividends);
  const shouldShowHeader = hasInfusions || hasRedemptions || hasCashPay || hasDividends;

  const handleTrancheIndex = (trancheIndex: number) => {
    setCurrentTrancheIndex(trancheIndex);
  };

  return (
    <>
      {shouldShowHeader && (
        <tr>
          <th
            className={classNames(styles['section-header'], styles['indentation-level-2'])}
            colSpan={titleColSpan}>
            <div className={styles['expand-toggle--indented']}>
              <SvgChevron
                onClick={() => handleToggle('exitDistributions')}
                className={
                  expanded.exitDistributions
                    ? styles['chevron-expanded']
                    : styles['chevron-collapsed']
                }
              />
            </div>
            {l('_ExitDistributions')}
          </th>
        </tr>
      )}
      {(shouldShowHeader ? expanded.exitDistributions : true) && (
        <>
          {!instrumentHasTranches && isOrdinaryEquity && (
            <TrancheLevelRow
              label={l('_PcShareholding', {
                instrument: instrumentName,
                interpolation: interpolationOption,
              })}
              valueKey="trancheParticipationPc"
              opmWaterfall={opmWaterfall}
              selectedYear={selectedYear}
              firstSimulation={firstSimulation}
              waterfallIterations={waterfallIterations}
              instrumentKey={instrumentKey}
              isPercentage
            />
          )}
          {instrumentHasTranches ? (
            <TrancheCalculationsErf
              opmWaterfall={opmWaterfall}
              selectedInstrument={instrumentKey}
              selectedYear={selectedYear}
              onTrancheIndex={handleTrancheIndex}
              instrument={instrument}
              instrumentHasDloc={instrumentHasDloc}
              firstSimulation={firstSimulation}
            />
          ) : (
            <InstrumentExitDistributionsTotals
              instrumentHasTranches={instrumentHasTranches}
              instrumentName={instrumentName}
              interpolationOption={interpolationOption}
              opmWaterfall={opmWaterfall}
              selectedYear={selectedYear}
              firstSimulation={firstSimulation}
              waterfallIterations={waterfallIterations}
              instrumentKey={instrumentKey}
              currentTrancheIndex={currentTrancheIndex}
              instrumentHasDloc={instrumentHasDloc}
            />
          )}
          <HorizontalTableDivider colSpan={titleColSpan} />
          {instrumentHasTranches && Object.keys(instrument.tranches).length > 1 && (
            <InstrumentLevelRow
              label={l('_TotalMVOfInstrument', {
                instrument: instrumentName,
                interpolation: interpolationOption,
              })}
              valueKey="discountedFairValue"
              opmWaterfall={opmWaterfall}
              selectedYear={selectedYear}
              waterfallIterations={waterfallIterations}
              instrumentKey={instrumentKey}
              isBold
            />
          )}
        </>
      )}
    </>
  );
};

export default InstrumentExitDistributions;
